/**
 * Progressive Chunks — renders content-chunk SSE data during loading.
 *
 * Sections appear one at a time as the API emits them:
 *   scripture → strongs → cross-refs → commentary
 *
 * Reuses existing section components for consistent rendering.
 */

import { useMemo } from 'react';
import type { ContentChunk } from '@state/chat';
import type { StrongsRef, CrossRef } from '@skjv/common';
import { ScriptureBlock } from './scripture-block';
import { StrongsSection } from './strongs-section';
import { CrossRefsSection } from './cross-refs-section';
import { CommentarySection } from './commentary-section';

interface ProgressiveChunksProps {
  chunks: ContentChunk[];
}

export function ProgressiveChunks({ chunks }: ProgressiveChunksProps) {
  const sections = useMemo(() => {
    const scripture = chunks.filter((c) => c.section === 'scripture');
    const strongs = chunks.filter((c) => c.section === 'strongs');
    const crossRefs = chunks.filter((c) => c.section === 'cross-refs');
    const commentary = chunks.filter((c) => c.section === 'commentary');

    return {
      scripture,
      strongsRefs: parseJson<StrongsRef[]>(strongs[0]?.content) ?? [],
      crossRefs: parseJson<CrossRef[]>(crossRefs[0]?.content) ?? [],
      commentaryText: commentary[0]?.content ?? '',
    };
  }, [chunks]);

  return (
    <div className="progressive-chunks">
      {sections.scripture.map((chunk, i) => (
        <div key={i} className="progressive-section">
          <ScriptureBlock text={chunk.content} reference={chunk.reference ?? ''} />
        </div>
      ))}

      {sections.strongsRefs.length > 0 && (
        <div className="progressive-section">
          <StrongsSection strongsRefs={sections.strongsRefs} title="Notable Words" />
        </div>
      )}

      {sections.crossRefs.length > 0 && (
        <div className="progressive-section">
          <CrossRefsSection crossRefs={sections.crossRefs} />
        </div>
      )}

      {sections.commentaryText && (
        <div className="progressive-section">
          <CommentarySection commentary={sections.commentaryText} />
        </div>
      )}
    </div>
  );
}

function parseJson<T>(json: string | undefined): T | null {
  if (!json) return null;
  try { return JSON.parse(json) as T; }
  catch { return null; }
}
